<template>
  <div>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <StatCard label="จำนวนครัวเรือนทั้งหมด" :value="totalHousehold" unit="ครัวเรือน" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <HouseIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="จำนวนสมาชิกเฉลี่ย" :value="avgMemberPerHH" unit="คน/ครัวเรือน" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <MemberIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
    </div>

    <SectionTitle title="ความมั่นคงทางเศรษฐกิจ" />
    <div class="grid grid-cols-1 sm:grid-cols-4 gap-4">
      <StatCard label="รายได้เฉลี่ย" :value="avgIncome" unit="บาท/ครัวเรือน/เดือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <CircleDollarSignBgIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="เงินออมสำรอง (เดือน)" :value="avgSaving" unit="เดือน/ครัวเรือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <SavingIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="รายจ่ายเฉลี่ย" :value="avgExpense" unit="บาท/ครัวเรือน/เดือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <ArrowDollarIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="หนี้สินเฉลี่ย" :value="avgDebt" unit="บาท/ครัวเรือน/เดือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <DeptIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
    </div>
    <div class="mt-4">
      <ChartCard title="รายได้เฉลี่ยต่อครัวเรือน">
        <div class="h-[400px]">
          <DashboardBarEChart
            chart-id="hhIncomeChart"
            type="bar"
            :data="incomeData"
            :options="dualAxisOptions"
            :plugins="[povertyLinePlugin]"
            fill-height
          />
        </div>
      </ChartCard>
    </div>

    <SectionTitle title="การศึกษาและทักษะที่จำเป็น" />
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="ระดับการศึกษาของสมาชิกในครัวเรือน">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhEduLevelChart"
              type="bar"
              :data="eduLevelData"
              :options="simpleBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="สถานะการศึกษา">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="hhEduStatusChart"
              :series="eduStatus.series"
              :labels="eduStatus.labels"
              unit="ครัวเรือน"
              :height="200"
              :show-data-label="true"
              :palette="eduStatusPalette"
              legendLayout="horizontal"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="มีสมาชิกออกจากการศึกษากลางคัน">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="hhDropoutChart"
              :series="dropout.series"
              :labels="dropout.labels"
              unit="ครัวเรือน"
              :height="200"
              :show-data-label="true"
              :palette="dropoutPalette"
              legendLayout="horizontal"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="สาเหตุในการออกจากการศึกษากลางคัน หรือไม่ได้เรียนต่อในระดับที่สูงกว่า">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhDropoutReasonChart"
              type="bar"
              :data="dropoutReasonData"
              :options="simpleBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <SectionTitle title="สถานะทางสุขภาพ" />
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <StatCard label="ครัวเรือนที่มีผู้ป่วย" :value="healthPatientHH" unit="ครัวเรือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <PatientIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="ครัวเรือนที่มีผู้พิการ" :value="healthDisabilityHH" unit="ครัวเรือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <DisabilityNewIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="ครัวเรือนที่ไม่มีผู้ป่วยหรือผู้พิการ" :value="healthNoneHH" unit="ครัวเรือน" align="left">
        <template #iconRight>
          <div class="w-6 h-6">
            <CircleXIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
    </div>
    <div class="grid grid-cols-12 gap-6 mt-4">
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ครัวเรือนที่มีสมาชิกมีโรคประจำตัวแยกตามประเภท">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhDiseaseChart"
              type="bar"
              :data="diseaseData"
              :options="simpleBarChartOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ครัวเรือนที่มีสมาชิกเป็นผู้พิการแยกตามประเภท">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhDisabilityChart"
              type="bar"
              :data="disabilityData"
              :options="simpleBarChartOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <SectionTitle title="คุณภาพชีวิตความเป็นอยู่" />
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="ประเภทที่อยู่อาศัย" class="h-full">
          <div class="h-[400px]">
            <ChartWithLegend
              variant="plain"
              chart-id="hhHomeTypeChart"
              :series="homeType.series"
              :labels="homeType.labels"
              unit="ครัวเรือน"
              :height="200"
              :show-data-label="true"
              :palette="['#3A506A', '#20A0D7', '#AF52DE', '#3F5CB6', '#606468']"
              legendLayout="horizontal"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-8">
        <ChartCard title="สถานภาพการครอบครองที่อยู่อาศัย" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhOwnershipChart"
              type="bar"
              :data="ownershipData"
              :options="simpleBarChartOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>

      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="แหล่งน้ำกิน น้ำใช้" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhWaterChart"
              type="bar"
              :data="waterData"
              :options="groupedOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="การเข้าถึงอินเทอร์เน็ต" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhInternetChart"
              type="bar"
              :data="internetData"
              :options="simpleBarChartOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="การเข้าถึงไฟฟ้า" class="h-full">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="hhPowerChart"
              :series="power.series"
              :labels="power.labels"
              unit="ครัวเรือน"
              :height="200"
              :show-data-label="true"
              :palette="powerPalette"
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <SectionTitle title="การเข้าถึงบริการภาครัฐ" />
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="สวัสดิการและความช่วยเหลือจากภาครัฐ" class="h-full">
          <div class="h-[450px]">
            <DashboardBarEChart
              chart-id="hhWelfareChart"
              type="bar"
              :data="welfareData"
              :options="welfareBarOptions"
              min-width="520px"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="แหล่งรับรู้ข่าวสารเกี่ยวกับการบริการ และให้ความช่วยเหลือ" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="hhNewsSourceChart"
              type="bar"
              :data="newsSourceData"
              :options="welfareBarOptions"
              min-width="420px"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import StatCard from "../container/StatCard.vue";
import SectionTitle from "../container/SectionTitle.vue";
import ChartCard from "../container/ChartCard.vue";
import ChartWithLegend from "../container/ChartWithLegend.vue";
import DashboardBarEChart from "../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";
import { shadeRange } from "@/utils/chartColor.js";

const C = {
  navy: "#3A506A",
  blue: "#20A0D7",
  yellow: "#F4D35E",
  green: "#11D67A",
  red: "#F73C4B",
};

const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data,
  barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const store = useStore();
const filterParams = computed(() => store.getters["dashboardOverviewFilter/getFilterForApi"]);
const authHeaders = () => ({
  Authorization: `Bearer ${store.getters["userAuth/getToken"]}`,
  "Content-Type": "application/json",
});
const apiGet = (path) =>
  axios.get(`${process.env.VUE_APP_BASE_API}${path}`, {
    headers: authHeaders(),
    params: filterParams.value,
  }).catch((err) => err);

const incomeRanges = ["ต่ำกว่า 5,000", "5,000-10,000", "10,000-15,000", "15,000-20,000", "20,000-25,000", "25,000-30,000", "30,000-35,000", "35,000-40,000", "40,000-45,000", "45,000-50,000", "50,000-65,000", "65,000-75,000", "75,000 ขึ้นไป"];
const incomeData = ref({ labels: [], datasets: [] });
const totalHousehold = ref(0);
const avgMemberPerHH = ref(0);
const avgIncome = ref(0);
const avgSaving = ref(0);
const avgDebt = ref(0);
const avgExpense = ref(0);

const healthPatientHH = ref(0);
const healthDisabilityHH = ref(0);
const healthNoneHH = ref(0);

const eduLevelData = ref({
  labels: [],
  datasets: [],
});
const eduStatus = ref({
  labels: [],
  series: [],
});
const EDU_STATUS_COLOR_BY_LABEL = { "ไม่เคยเรียน": "#F73C4B", "ไม่เรียนแล้ว": "#F4D35E", "กำลังเรียนอยู่": "#11d67a" };
const eduStatusPalette = computed(() =>
  eduStatus.value.labels.map((label) => EDU_STATUS_COLOR_BY_LABEL[label])
);
const dropout = ref({
  labels: [],
  series: [],
});
const DROPOUT_COLOR_BY_LABEL = { "ออกกลางคัน 1 คน": "#FE9000", "ออกกลางคัน 2 คนขึ้นไป": "#F73C4B", "ไม่มี": "#11D67A" };
const dropoutPalette = computed(() =>
  dropout.value.labels.map((label) => DROPOUT_COLOR_BY_LABEL[label] || "#606468")
);
const dropoutReasonData = ref({
  labels: [],
  datasets: [],
});

const diseaseData = ref({
  labels: [],
  datasets: [],
});
const disabilityData = ref({
  labels: [],
  datasets: [],
});
const fetchHouseSummary = async () => {
  const res = await apiGet("house/survey/dashboard/householdSummary");
  
  if (res?.status === 200) {
    totalHousehold.value = globalNumDecFixed(res.data.total_household || 0);
    avgMemberPerHH.value = globalNumDecFixed(res.data.avg_member_per_household || 0);
  }
};

const homeType = ref({ labels: [], series: [] });
const ownershipData = ref({ labels: [], datasets: [] });
const waterData = ref({ labels: [], datasets: [] });
const internetData = ref({ labels: [], datasets: [] });
const internetDisplay = [
  { key: "i1", display: ["เข้าถึงจากเครือข่าย", "โทรศัพท์มือถือ"] },
  { key: "i2", display: ["เข้าถึงจาก", "Wifi ส่วนตัว"] },
  { key: "i3", display: ["เข้าถึงจาก", "Wifi สาธารณะ"] },
  { key: "i4", display: ["ไม่สามารถเข้าถึงเนื่องจาก", "ไม่มีกำลังทรัพย์"] },
  { key: "i5", display: ["ไม่สามารถเข้าถึงเนื่องจาก", "ไม่ต้องการใช้งาน"] },
  { key: "i6", display: ["ไม่สามารถเข้าถึงเนื่องจาก", "ไม่มีเครือข่ายรองรับ"] },
];
const internetMap = {
  "เข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยจากเครือข่ายโทรศัพท์มือถือ": "i1",
  "เข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยจาก Wifi ส่วนตัว": "i2",
  "เข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยจาก Wifi สาธารณะ": "i3",
  "ไม่สามารถเข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยเนื่องจากไม่มีกําลังทรัพย์เพียงพอ": "i4",
  "ไม่สามารถเข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยเนื่องจากไม่ต้องการใช้งาน": "i5",
  "ไม่สามารถเข้าถึงสัญญาณอินเทอร์เน็ตภายในที่อยู่อาศัยเนื่องจากไม่มีเครือข่ายรองรับ": "i6",
};
const INTERNET_COLOR_BY_KEY = {
  i1: "#11D67A",
  i2: "#20A0D7",
  i3: "#3F5CB6",
  i4: "#F73C4B",
  i5: "#FE9000",
  i6: "#F4D35E",
};
const power = ref({ labels: [], series: [] });
const POWER_COLOR_BY_LABEL = {
  "มีไฟฟ้าใช้และสามารถชำระค่าไฟโดยตรงกับหน่วยงานของรัฐ": "#11D67A",
  "มีไฟฟ้าใช้ แต่ไม่มีมิเตอร์จากหน่วยงานของรัฐต้องชำระค่าไฟผ่านเพื่อนบ้าน/เอกชน/คนกลาง": "#20A0D7",
  "ไม่มีไฟฟ้าใช้เลย": "#F73C4B",
  "ใช้แสงสว่างอื่น อาทิ เทียน ตะเกียง แบตเตอรี่ เครื่องปั่นไฟ โซลาร์เซลล์": "#F4D35E",
};
const powerPalette = computed(() =>
  power.value.labels.map((label) => POWER_COLOR_BY_LABEL[label] || "#606468")
);
const welfareData = ref({ labels: [], datasets: [] });
const newsSource = ref({ labels: [], series: [] });
const NEWS_SOURCE_NO_INFO_LABEL = "ไม่ได้รับข่าวสาร";
const NEWS_SOURCE_NO_INFO_COLOR = "#8E2439";
const NEWS_SOURCE_THEME_PALETTE = ["#1B9AAA", "#6C63B5", "#C08A3E", "#7A9E7E", "#5B7C99", "#4A4E58"];
const newsSourceData = computed(() => {
  let themeIdx = 0;
  const colors = newsSource.value.labels.map((label) => {
    if (label === NEWS_SOURCE_NO_INFO_LABEL) return NEWS_SOURCE_NO_INFO_COLOR;
    const color = NEWS_SOURCE_THEME_PALETTE[themeIdx % NEWS_SOURCE_THEME_PALETTE.length];
    themeIdx++;
    return color;
  });
  return {
    labels: newsSource.value.labels,
    datasets: [bar("จำนวนครัวเรือน", "#1B9AAA", newsSource.value.series)],
  };
});
const fetchIncome = async () => {
  const res = await apiGet("house/survey/dashboard/economicSecurity");
  if (res?.status === 200) {
    const d = res.data || {};
    const overall = d.overall || {};
    const byIncome = Array.isArray(d.by_income) ? d.by_income : [];

    avgIncome.value = Number(Number(overall.avg_income || 0).toFixed(2));
    avgSaving.value = Number(Number(overall.avg_saving || 0).toFixed(2));
    avgDebt.value = Number(Number(overall.avg_debt || 0).toFixed(2));
    avgExpense.value = Number(Number(overall.avg_expense || 0).toFixed(2));

    const labels = incomeRanges;
    const values = labels.map((label) => {
      const normalizedLabel = label.replace(/[–—−]/g, "-");
      const match = byIncome.find((item) => {
        const normalizedItemName = (item.name || "").replace(/[–—−]/g, "-");
        return normalizedItemName === normalizedLabel;
      });
      return match ? Math.round(Number(match.avg_income) || 0) : 0;
    });

    incomeData.value = {
      labels,
      datasets: [
        bar("รายได้เฉลี่ย", "#4e708c", values),
        {
          type: "line",
          label: "เส้นความยากจน",
          borderColor: "#3A506A",
          borderDash: [6, 6],
          borderWidth: 0,
          pointRadius: 0,
          hitRadius: 0,
          data: labels.map(() => 3078),
        },
      ],
    };
  }
};
const fetchUtility = async () => {
  const res = await apiGet("house/survey/dashboard/qualityOfLife");
  if (res?.status === 200) {
    const d = res.data || {};

    if (Array.isArray(d.by_house_type)) {
      homeType.value = {
        labels: d.by_house_type.map((item) => item.name || ""),
        series: d.by_house_type.map((item) => Number(item.total || 0)),
      };
    }
    const houseOwnerDisplay = [
      { key: "d1", display: ["มีบ้านและที่ดินเป็นของตนเองโดยไม่ต้อง", "ผ่อนชําระและไม่ติดจํานองหรือค้ําประกัน"] },
      { key: "d2", display: ["อาศัยในบ้านพักสวัสดิการตามสิทธิที่", "ตนเอง หรือสมาชิกในครัวเรือนได้รับ"] },
      { key: "d3", display: ["มีบ้านและที่ดินเป็นของตนเอง", "แต่ติดจํานองหรือค้ำประกัน"] },
      { key: "d4", display: ["อาศัยหรือปลูกบ้านในที่ดินผู้อื่น อาทิ ญาติ", " ที่ดินสาธารณะ ที่ดินของหน่วยงานภาค", "รัฐภาคเอกชน ศาสนสถาน"] },
      { key: "d5", display: ["อาศัยอยู่กับผู้อื่นโดยไม่เสียค่าเช่า"] },
      { key: "d6", display: ["เช่าบ้านอยู่"] },
      { key: "d7", display: ["มีบ้านและที่ดินเป็นของตนเอง", "แต่อยู่ระหว่างการผ่อนชําระ"] },
      { key: "d8", display: ["ไม่มีบ้านพักอาศัยเป็นหลักแหล่ง"] },
      { key: "other", display: ["อื่นๆ"] }
    ]

    const houseOwnerMap = {
      "มีบ้านและที่ดินเป็นของตนเองโดยไม่ต้องผ่อนชําระและไม่ติดจํานองหรือค้ําประกัน": "d1",
      "อาศัยในบ้านพักสวัสดิการตามสิทธิที่ตนเอง หรือสมาชิกในครัวเรือนได้รับ": "d2",
      "มีบ้านและที่ดินเป็นของตนเองแต่ติดจํานองหรือค้ำประกัน": "d3",
      "อาศัยหรือปลูกบ้านในที่ดินผู้อื่น อาทิ ญาติ ที่ดินสาธารณะ ที่ดินของหน่วยงานภาครัฐภาคเอกชน ศาสนสถาน": "d4",
      "อาศัยอยู่กับผู้อื่นโดยไม่เสียค่าเช่า": "d5",
      "เช่าบ้านอยู่": "d6",
      "มีบ้านและที่ดินเป็นของตนเองแต่อยู่ระหว่างการผ่อนชําระ": "d8",
      "ไม่มีบ้านพักอาศัยเป็นหลักแหล่ง": "d9",
      "อื่นๆ": "other"
    }

    if (Array.isArray(d.by_house_ownership)) {
      const ownershipLabels = houseOwnerDisplay.map((item) => item.display);
      const houseOwnerValues = houseOwnerDisplay.map((dp) =>
        Number(
          d.by_house_ownership.find(
            (item) => houseOwnerMap[(item.name || "")] === dp.key
          )?.total || 0
        )
      );
      ownershipData.value = {
        labels: ownershipLabels,
        datasets: [bar("จำนวนครัวเรือน", "#6c63b5", houseOwnerValues)],
      };
    }

    const drinkData = Array.isArray(d.by_water_drink) ? d.by_water_drink : [];
    const usedData = Array.isArray(d.by_water_used) ? d.by_water_used : [];
    const waterSources = [...new Map([...drinkData, ...usedData].map((item) => [item.name || "", item.name || ""])).values()];
    const waterColors = ["#3A506A", "#20A0D7", "#11D67A", "#AF52DE", "#606468"];

    waterData.value = {
      labels: ["น้ำกิน", "น้ำใช้"],
      datasets: waterSources.map((sourceName, index) => {
        const drinkCount = drinkData.find((item) => (item.name || "") === sourceName)?.total || 0;
        const usedCount = usedData.find((item) => (item.name || "") === sourceName)?.total || 0;
        return bar(sourceName, waterColors[index % waterColors.length], [drinkCount, usedCount], {
          barPercentage: 0.8,
          categoryPercentage: 0.6,
        });
      }),
    };

    if (Array.isArray(d.by_electricity)) {
      power.value = {
        labels: d.by_electricity.map((item) => item.name || ""),
        series: d.by_electricity.map((item) => Number(item.total_household || 0)),
      };
    }

    if (Array.isArray(d.by_internet)) {
      const internetValues = internetDisplay.map((dp) =>
        Number(
          d.by_internet.find(
            (item) => internetMap[(item.name || "")] === dp.key
          )?.total_household || 0
        )
      );
      internetData.value = {
        labels: internetDisplay.map((item) => item.display),
        datasets: [bar("จำนวนครัวเรือน", internetDisplay.map((dp) => INTERNET_COLOR_BY_KEY[dp.key]), internetValues)],
      };
    }
  }
};
const fetchEducation = async () => {
  const res = await apiGet("house/survey/dashboard/education");
  if (res?.status === 200) {
    const d = res.data || {};
    const eduLevel = Array.isArray(d.by_edu_group) ? d.by_edu_group : [];
    const eduStatusData = Array.isArray(d.by_study_status) ? d.by_study_status : [];
    const dropoutCauses = Array.isArray(d.by_dropout_causes) ? d.by_dropout_causes : [];
    const dropoutSummary = d.dropout || {};

    eduLevelData.value = {
      labels: eduLevel.map((item) => item.name || ""),
      datasets: [bar("จำนวน", "#22ac6b", eduLevel.map((item) => Number(item.total || 0)))],
    };

    eduStatus.value = {
      labels: eduStatusData.map((item) => item.name || ""),
      series: eduStatusData.map((item) => Number(item.total || 0)),
    };

    const dropoutLabels = ["ออกกลางคัน 1 คน", "ออกกลางคัน 2 คนขึ้นไป", "ไม่มี"];
    const dropoutSeries = [
      Number(dropoutSummary.dropout_one || 0),
      Number(dropoutSummary.dropout_two_plus || 0),
      Number(dropoutSummary.dropout_none || 0),
    ];

    const dropoutData = dropoutLabels.map((label, index) => ({
      label,
      value: dropoutSeries[index],
    }));

    dropout.value = {
      labels: dropoutData.map((item) => item.label),
      series: dropoutData.map((item) => item.value),
    };

    const dropoutCauseDisplay = [
      { key: "d1", display: ["ไม่สามารถรับภาระค่า", "ใช้จ่ายจากการศึกษาได้"] },
      { key: "d2", display: ["เพื่อไปประกอบอาชีพ", "หรือไปฝึกอบรมทักษะอาชีพ"] },
      { key: "d3", display: ["ต้องช่วยงานบ้าน (อาทิ ดูแลผู้", "สูงอายุหรือเด็กเล็ก ถูบ้าน ซักผ้า)"] },
      { key: "d4", display: ["สมรสหรือมีบุตร"] },
      { key: "d5", display: ["สิ่งที่เรียนไม่สอดคล้องกับความต้อง", "การ เบื่อ ไม่อยากเรียน ไม่ชอบเรียน"] },
      { key: "d6", display: ["ครอบครัวไม่สนับสนุนให้เรียน", " ไม่เห็นความสำคัญของการศึกษา"] },
      { key: "d7", display: ["มีปัญหาด้านสุขภาพ/มีความพิการ"] },
      { key: "other", display: ["อื่นๆ"] },
    ];

    const dropoutCauseMap = {
      "ไม่สามารถรับภาระค่าใช้จ่ายจากการศึกษาได้": "d1",
      "เพื่อไปประกอบอาชีพ หรือไปฝึกอบรมทักษะอาชีพ": "d2",
      "ต้องช่วยงานบ้าน (อาทิ ดูแลผู้สูงอายุหรือเด็กเล็ก ถูบ้าน ซักผ้า)": "d3",
      "สมรสหรือมีบุตร": "d4",
      "สิ่งที่เรียนไม่สอดคล้องกับความต้องการ เบื่อ ไม่อยากเรียน ไม่ชอบเรียน": "d5",
      "ครอบครัวไม่สนับสนุนให้เรียน ไม่เห็นความสำคัญของการศึกษา": "d6",
      "มีปัญหาด้านสุขภาพ/มีความพิการ": "d7",
      "อื่น ๆ": "other",
      "อื่นๆ": "other",
    };

    const dropoutReasonLabels = dropoutCauseDisplay.map((item) => item.display);
    const dropoutReasonValues = dropoutCauseDisplay.map((d) =>
      Number(
        dropoutCauses.find(
          (item) => dropoutCauseMap[(item.name || "")] === d.key
        )?.count || 0
      )
    );

    dropoutReasonData.value = {
      labels: dropoutReasonLabels,
      datasets: [bar("จำนวนครัวเรือน", "#f69757", dropoutReasonValues)],
    };
  }
};

const fetchHealth = async () => {
  const res = await apiGet("house/survey/dashboard/health");
  if (res?.status === 200) {
    const d = res.data || {};
    const summary = d.summary || {};
    const illTypes = Array.isArray(d.by_ill_type) ? d.by_ill_type : [];
    const disabledTypes = Array.isArray(d.by_disability_category) ? d.by_disability_category : [];

    healthPatientHH.value = Number(summary.with_ill || 0);
    healthDisabilityHH.value = Number(summary.with_disabled || 0);
    healthNoneHH.value = Number(summary.without_disabled || 0);

    const diseaseDisplay = [
      { key: "d1", display: ["ไม่มีโรคประจำตัวหรือ", "ความผิดปกติร้ายแรงใดๆ"] },
      { key: "d2", display: ["มีโรคประจำตัว กลุ่มโรค", "ไม่ติดต่อเรื้อรัง (NCDs)"] },
      { key: "d3", display: ["มีโรคประจำตัว กลุ่มโรคเกี่ยวกับ", "อวัยวะและระบบการทำงานที่สำคัญ"] },
      { key: "d4", display: ["มีโรคประจำตัว กลุ่มโรคหลอดเลือดสมอง", "ระบบประสาท/กล้ามเนื้อ และภาวะติดเชื้อ"] },
      { key: "d5", display: ["มีโรคประจำตัว กลุ่มการบาดเจ็บ", "ร้ายแรงและภาวะทุพพลภาพ"] },
      { key: "d6", display: ["มีอาการด้วยโรคจิตเวช"] },
      { key: "other", display: ["อื่นๆ"] },
    ];

    const diseaseMap = {
      "ไม่มีโรคประจำตัวหรือความผิดปกติร้ายแรงใดๆ": "d1",
      "มีโรคประจำตัว กลุ่มโรคไม่ติดต่อเรื้อรัง (NCDs)": "d2",
      "มีโรคประจำตัว กลุ่มโรคเกี่ยวกับอวัยวะและระบบการทำงานที่สำคัญ": "d3",
      "มีโรคประจำตัว กลุ่มโรคหลอดเลือดสมอง ระบบประสาท/กล้ามเนื้อ และภาวะติดเชื้อ": "d4",
      "มีโรคประจำตัว กลุ่มการบาดเจ็บร้ายแรงและภาวะทุพพลภาพ": "d5",
      "มีอาการด้วยโรคจิตเวช": "d6",
      "มีโรคประจำตัวอื่น ๆ โปรดระบุ": "other"
    }

    const diseaseLabels = diseaseDisplay.map((item) => item.display);
    diseaseData.value = {
      labels: diseaseLabels,
      datasets: [bar("จำนวนครัวเรือน", "#d6b170", diseaseDisplay.map((d) =>
        Number(
          illTypes.find(
            (item) => diseaseMap[(item.name || "")] === d.key
          )?.count || 0
        )
      ))],
    };

    disabilityData.value = {
      labels: disabledTypes.map((item) => [item.type || ""]),
      datasets: [bar("จำนวนครัวเรือน", "#5b7fc7", disabledTypes.map((item) => Number(item.households_count || 0)))],
    };
  }
};
const fetchWelfare = async () => {
  const res = await apiGet("house/survey/dashboard/welfareInformation");
  if (res?.status === 200) {
    const d = res.data || {};
    const welfareItems = Array.isArray(d.welfare) ? d.welfare : [];
    const infoItems = Array.isArray(d.welfare_information) ? d.welfare_information : [];
    const welfareDisplay = [
      { key: "d1", display: ["ไม่ได้รับข่าวสาร"] },
      { key: "d2", display: ["เงินอุดหนุนเพื่อการเลี้ยงดู", "เด็กแรกเกิด"] },
      { key: "d3", display: ["บัตรสวัสดิการแห่งรัฐ"] },
      { key: "d4", display: ["เบี้ยยังชีพผู้สูงอายุ"] },
      { key: "d5", display: ["เบี้ยความพิการ"] },
      { key: "d6", display: ["โครงการคนละครึ่ง"] },
      { key: "d7", display: ["โครงการเราชนะ"] },
      { key: "d8", display: ["ทุนการศึกษาแบบไม่ต้องใช้คืน"] },
      { key: "d9", display: ["โครงการเรารักกัน"] },
      { key: "d10", display: ["เงินช่วยเหลือจากกองทุนคุ้มครองเด็ก"] },
      { key: "d11", display: ["โครงการยิ่งใช้ยิ่งได้"] },
      { key: "d12", display: ["เงินสงเคราะห์เบี้ยยังชีพผู้ป่วยเอดส์"] },
      { key: "other", display: ["สวัสดิการช่วยเหลืออื่น ๆ"] }
    ]

    const welfareMap = {
      "ไม่ได้รับข่าวสาร": "d1",
      "เงินอุดหนุนเพื่อการเลี้ยงดูเด็กแรกเกิด": "d2",
      "บัตรสวัสดิการแห่งรัฐ": "d3",
      "เบี้ยยังชีพผู้สูงอายุ": "d4",
      "เบี้ยความพิการ": "d5",
      "โครงการคนละครึ่ง": "d6",
      "โครงการเราชนะ": "d7",
      "ทุนการศึกษาแบบไม่ต้องใช้คืน (รวมจากทุกหน่วยงาน อาทิ โรงเรียน กองทุนเพื่อความเสมอภาคทางการศึกษาและ/หรือองค์กรปกครองส่วนท้องถิ่น": "d8",
      "โครงการเรารักกัน": "d9",
      "เงินช่วยเหลือจากกองทุนคุ้มครองเด็ก": "d10",
      "โครงการยิ่งใช้ยิ่งได้": "d11",
      "เงินสงเคราะห์เบี้ยยังชีพผู้ป่วยเอดส์": "d12",
      "สวัสดิการช่วยเหลืออื่น ๆ": "other"
    }


    welfareData.value = {
      labels: welfareDisplay.map((item) => item.display),
      datasets: [bar("จำนวน", "#1f6f8b", welfareDisplay.map((dp) => Number(
        welfareItems.find(
          (item) => welfareMap[(item.name || "")] === dp.key)?.total_household || 0)
        )
      )],
    };

    newsSource.value = {
      labels: infoItems.map((item) => item.name || ""),
      series: infoItems.map((item) => Number(item.total_household || 0)),
    };
  }
};


onMounted(() => {
  fetchHouseSummary();
  fetchIncome();
  fetchEducation();
  fetchHealth();
  fetchUtility();
  fetchWelfare();
});
watch(filterParams, ()=> {
  fetchHouseSummary();
  fetchIncome();
  fetchEducation();
  fetchHealth();
  fetchUtility();
  fetchWelfare();
}, { deep: true })

const baseOptions = {
  responsive: true,
  aspectRatio: 2,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: false, boxWidth: 30, boxHeight: 8, font: { size: 12 } },
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(ctx) {
          return `${ctx.dataset.label}: ${globalNumDecFixed(ctx.parsed.y)} ครัวเรือน`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45, minRotation: 45 } },
    y: { beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } },
  },
};

const simpleBarOptions = {
  ...baseOptions,
  aspectRatio: 1.6,
  plugins: { ...baseOptions.plugins, legend: { display: false } },
};

const simpleBarChartOptions = { ...simpleBarOptions, maintainAspectRatio: false };

const welfareBarOptions = {
  ...simpleBarChartOptions,
  scales: {
    ...simpleBarChartOptions.scales,
    x: {
      ...simpleBarChartOptions.scales.x,
      ticks: { ...simpleBarChartOptions.scales.x.ticks, autoSkip: false },
    },
  },
};

const groupedOptions = {
  ...baseOptions,
  aspectRatio: 2.4,
  plugins: {
    ...baseOptions.plugins,
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: false, boxWidth: 30, boxHeight: 8, font: { size: 12 } },
    },
  },
};

const povertyLinePlugin = {
  id: "povertyLine",
  afterDraw(chart) {
    const idx = chart.data.datasets.findIndex((ds) => ds.type === "line");
    if (idx === -1 || !chart.isDatasetVisible(idx)) return;
    const ds = chart.data.datasets[idx];
    const value = ds.data?.[0];
    if (!value) return;
    const { ctx, scales: { y }, chartArea: { left, right } } = chart;
    if (!y) return;
    const yPx = y.getPixelForValue(value);
    const headLen = 8, headHalf = 4;
    const arrowStart = right + 6, arrowEnd = right + 24;
    ctx.save();
    ctx.strokeStyle = "#3A506A";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath(); ctx.moveTo(left, yPx); ctx.lineTo(right, yPx); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(arrowStart, yPx); ctx.lineTo(arrowEnd - headLen, yPx); ctx.stroke();
    ctx.fillStyle = "#3A506A";
    ctx.beginPath();
    ctx.moveTo(arrowEnd, yPx);
    ctx.lineTo(arrowEnd - headLen, yPx - headHalf);
    ctx.lineTo(arrowEnd - headLen, yPx + headHalf);
    ctx.closePath(); ctx.fill();
    ctx.font = "12px Kanit, sans-serif";
    ctx.fillStyle = "#3A506A";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(ds.label, arrowEnd + 4, yPx - 14);
    ctx.fillText(globalNumDecFixed(value), arrowEnd + 4, yPx + 3);
    ctx.fillText(`บาท`, arrowEnd + 4, yPx + 18);
    ctx.restore();
  },
};

const dualAxisOptions = {
  ...baseOptions,
  aspectRatio: 3,
  layout: { padding: { right: 120 } },
  plugins: {
    ...baseOptions.plugins,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        usePointStyle: true,
        boxWidth: 30,
        boxHeight: 8,
        font: { size: 12 },
        generateLabels(chart) {
          return chart.data.datasets.map((ds, i) => ({
            text: ds.type === "line"
                ? `${ds.label} (${globalNumDecFixed(3078)} บาท)`
                : ds.label,
            datasetIndex: i,
            hidden: !chart.isDatasetVisible(i),
            fillStyle: ds.type === "line" ? "transparent" : ds.backgroundColor,
            strokeStyle: ds.borderColor || ds.backgroundColor,
            lineWidth: ds.type === "line" ? (ds.borderWidth || 1.5) : 0,
            lineDash: ds.borderDash || [],
            pointStyle: ds.type === "line" ? "line" : "rect",
          }));
        },
      },
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(ctx) {
          const val = globalNumDecFixed(ctx.parsed.y);
          return `${ctx.dataset.label}: ${val} บาท`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45, minRotation: 45 } },
    y: { position: "left", beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 }, title: { display: true, text: "บาท", font: { size: 12 } } },
  },
};
</script>
